import DxfParser from 'dxf-parser';
import type {
  IArcEntity,
  ICircleEntity,
  IDxf,
  IEntity,
  IInsertEntity,
  ILineEntity,
  ILwpolylineEntity,
  IMtextEntity,
  IPolylineEntity,
  ITextEntity,
} from 'dxf-parser';
import AutoCadColorIndex from 'dxf-parser/dist/AutoCadColorIndex';
import type {
  GeomEntity,
  Geometry,
  Vec2,
} from '../shared/types';
import { profileArea } from './benchProfile';
import {
  computeBounds,
  degToRad,
} from './geometry';
import { parseHatches } from './dxfHatch';

function toHex(n: number): string {
  const v = (n >>> 0) & 0xffffff;
  return '#' + v.toString(16).padStart(6, '0');
}

function aciToHex(idx: number): string | undefined {
  if (!Number.isFinite(idx) || idx <= 0 || idx >= 256) return undefined;
  const v = AutoCadColorIndex[idx];
  return typeof v === 'number' ? toHex(v) : undefined;
}

/** Pure black is the "default" pen on white paper; keep it visible on our dark
 *  canvas by leaving the colour unset (the renderer falls back to the theme). */
function normalize(hex?: string): string | undefined {
  return !hex || hex === '#000000' ? undefined : hex;
}

function layerColor(dxf: IDxf, layer?: string): string | undefined {
  const layers = dxf.tables?.layer?.layers;
  const rec = layer && layers ? (layers as Record<string, { color?: number }>)[layer] : undefined;
  return rec && typeof rec.color === 'number' ? toHex(rec.color) : undefined;
}

/** Resolve an entity's colour, honouring BYLAYER (colorIndex 0/256/absent). */
function resolveColor(ent: IEntity, dxf: IDxf): string | undefined {
  const e = ent as IEntity & { color?: number; colorIndex?: number; layer?: string };
  const ci = e.colorIndex;
  const byLayer = ci === undefined || ci === 0 || ci === 256;
  if (!byLayer && typeof e.color === 'number') return normalize(toHex(e.color));
  const fromLayer = layerColor(dxf, e.layer);
  if (fromLayer) return normalize(fromLayer);
  return typeof e.color === 'number' ? normalize(toHex(e.color)) : undefined;
}

interface Transform {
  /** translation */
  tx: number;
  ty: number;
  /** uniform-ish scale */
  sx: number;
  sy: number;
  /** rotation in degrees */
  rot: number;
}

const IDENTITY: Transform = { tx: 0, ty: 0, sx: 1, sy: 1, rot: 0 };

/** Round to 0.01 mm. Drawings are in millimetres, so this is far finer than any
 *  real precision yet trims the stored/serialised coordinates substantially. */
function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

function apply(t: Transform, x: number, y: number): Vec2 {
  const a = degToRad(t.rot);
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const px = x * t.sx;
  const py = y * t.sy;
  return {
    x: round2(px * cos - py * sin + t.tx),
    y: round2(px * sin + py * cos + t.ty),
  };
}

/**
 * Entity handle → OCS extrusion Z, scanned from the raw DXF. dxf-parser parses
 * the extrusion (group 230) for ARC but NOT for CIRCLE, so a circle drawn in a
 * mirrored frame (extrusion (0,0,-1)) loses the flag and lands at the wrong X.
 * We recover it from the raw text and key it by the entity handle (group 5).
 */
let extrusionZByHandle: Map<string, number> = new Map();

function scanExtrusionZByHandle(text: string): Map<string, number> {
  const map = new Map<string, number>();
  const lines = text.split(/\r\n|\r|\n/);
  let handle: string | null = null;
  // DXF is strictly alternating code/value lines; step in pairs.
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = lines[i].trim();
    const value = lines[i + 1];
    if (code === '0') handle = null; // entity boundary: forget the previous handle
    else if (code === '5' && handle === null) handle = value.trim(); // first handle wins
    else if (code === '230' && handle) {
      const z = parseFloat(value);
      if (!Number.isNaN(z)) map.set(handle, z);
    }
  }
  return map;
}

/** Whether an entity's OCS mirrors the X axis (extrusion Z < 0), via dxf-parser
 *  or, when it dropped the flag, the raw-text handle scan. */
function ocsFlipX(ent: IEntity): boolean {
  const e = ent as { extrusionDirectionZ?: number; handle?: string | number };
  const z = e.extrusionDirectionZ ?? (e.handle != null ? extrusionZByHandle.get(String(e.handle)) : undefined);
  return typeof z === 'number' && z < 0;
}

/**
 * Parse DXF text into normalized geometry. Block references (INSERT) are
 * expanded one level deep with their transform applied.
 *
 * HATCH entities are discarded by default: the minimal raw-text hatch reader is
 * unreliable across DXF variants, so opening a drawing skips fills rather than
 * importing them wrong. Pass `includeHatches` to opt back in.
 */
export function parseDxf(dxfText: string, includeHatches = false): Geometry {
  const parser = new DxfParser();
  const dxf = parser.parseSync(dxfText);
  if (!dxf) throw new Error('Could not parse DXF data.');
  extrusionZByHandle = scanExtrusionZByHandle(dxfText);

  const out: GeomEntity[] = [];
  for (const ent of dxf.entities || []) {
    convertEntity(ent, dxf, IDENTITY, out, 0);
  }

  // HATCH entities (filled regions / retini): dxf-parser drops these, so we scan
  // the raw text for them. They go first so the fills render behind the line
  // work. Only included when explicitly requested (unreliable across variants).
  const hatches: GeomEntity[] = [];
  if (includeHatches) {
    for (const h of parseHatches(dxfText)) {
      if (!h.loops.length) continue;
      hatches.push({ kind: 'hatch', loops: h.loops, solid: h.solid, color: resolveHatchColor(h, dxf) });
    }
  }

  const entities = [...hatches, ...out];
  return { entities, bounds: computeBounds(entities) };
}

function resolveHatchColor(h: { trueColor?: number; colorIndex?: number; layer?: string }, dxf: IDxf): string | undefined {
  if (typeof h.trueColor === 'number') return normalize(toHex(h.trueColor));
  if (typeof h.colorIndex === 'number') {
    const hex = aciToHex(h.colorIndex);
    if (hex) return normalize(hex);
  }
  return normalize(layerColor(dxf, h.layer));
}

/**
 * Whether an entity should be imported: hidden entities (group code 60 = 1) and
 * those on an off/frozen layer are skipped, so blocks bring in their visible
 * parts only.
 */
function entityVisible(ent: IEntity, dxf: IDxf): boolean {
  if (ent.visible === false) return false;
  const layers = dxf.tables?.layer?.layers as Record<string, { visible?: boolean; frozen?: boolean }> | undefined;
  const rec = ent.layer && layers ? layers[ent.layer] : undefined;
  if (rec && (rec.visible === false || rec.frozen === true)) return false;
  return true;
}

function convertEntity(
  ent: IEntity,
  dxf: IDxf,
  t: Transform,
  out: GeomEntity[],
  depth: number,
  inherited?: string
): void {
  if (!entityVisible(ent, dxf)) return; // skip hidden / off-layer / frozen parts
  const color = resolveColor(ent, dxf) ?? inherited;
  switch (ent.type) {
    case 'LINE': {
      const e = ent as ILineEntity;
      if (e.vertices && e.vertices.length >= 2) {
        out.push({
          kind: 'line',
          a: apply(t, e.vertices[0].x, e.vertices[0].y),
          b: apply(t, e.vertices[1].x, e.vertices[1].y),
          color,
        });
      }
      break;
    }
    case 'LWPOLYLINE': {
      const e = ent as ILwpolylineEntity;
      const points = (e.vertices || []).map((v) => apply(t, v.x, v.y));
      if (points.length >= 2) {
        out.push({ kind: 'polyline', points, closed: !!e.shape, color });
      }
      break;
    }
    case 'POLYLINE': {
      const e = ent as IPolylineEntity;
      const points = (e.vertices || []).map((v) => apply(t, v.x, v.y));
      if (points.length >= 2) {
        out.push({ kind: 'polyline', points, closed: !!e.shape, color });
      }
      break;
    }
    case 'CIRCLE': {
      const e = ent as ICircleEntity;
      // OCS: an extrusion of (0,0,-1) mirrors the entity's local X axis, so the
      // true X of the centre is -center.x (see ARC below).
      const flipX = ocsFlipX(ent);
      out.push({
        kind: 'circle',
        center: apply(t, flipX ? -e.center.x : e.center.x, e.center.y),
        radius: round2(e.radius * Math.abs(t.sx)),
        color,
      });
      break;
    }
    case 'ARC': {
      const e = ent as IArcEntity;
      // dxf-parser returns ARC angles in RADIANS; our geometry/renderer use
      // degrees. Converting is essential: left in radians, an arc whose end
      // angle is the smaller value makes the renderer wrap the sweep by +360°
      // and draw a near-full circle instead of the arc.
      const radToDeg = 180 / Math.PI;
      // OCS handling: AutoCAD stores arc/circle centres and angles in the
      // entity's Object Coordinate System. An extrusion of (0,0,-1) mirrors the
      // local X axis, so the centre's true X is -center.x and the arc, drawn CCW
      // in OCS, becomes CW — its start/end angles reflect to 180°-angle and
      // swap. Without this the arc lands at the wrong (negative) X, offset to
      // the left. LINE/LWPOLYLINE carry WCS points, so they're unaffected.
      const flipX = ocsFlipX(ent);
      let sa = e.startAngle * radToDeg;
      let ea = e.endAngle * radToDeg;
      if (flipX) { const s = 180 - ea; ea = 180 - sa; sa = s; }
      out.push({
        kind: 'arc',
        center: apply(t, flipX ? -e.center.x : e.center.x, e.center.y),
        radius: round2(e.radius * Math.abs(t.sx)),
        startAngle: sa + t.rot,
        endAngle: ea + t.rot,
        color,
      });
      break;
    }
    case 'ELLIPSE': {
      const e = ent as unknown as { center?: Vec2; majorAxisEndPoint?: Vec2; axisRatio?: number; startAngle?: number; endAngle?: number };
      if (!e.center || !e.majorAxisEndPoint) break;
      const ratio = typeof e.axisRatio === 'number' ? e.axisRatio : 1;
      const start = typeof e.startAngle === 'number' ? e.startAngle : 0;
      let span = (typeof e.endAngle === 'number' ? e.endAngle : Math.PI * 2) - start;
      while (span <= 1e-9) span += Math.PI * 2;
      const full = span >= Math.PI * 2 - 1e-6;
      // NB: no OCS X-mirror here. dxf-parser doesn't surface ELLIPSE extrusion
      // (its centre stays in raw OCS), and real files carry a (0,0,-1) extrusion
      // on ellipses whose centre is NOT pre-mirrored — applying the flip then
      // pushes them off to the wrong side. We keep the centre as given, matching
      // how the library (and these files) expect ellipses to be placed.
      // Parametric: transform the centre and the major-axis vector to world (a
      // rotation/uniform-scale keeps it an ellipse; the parameter angles are
      // relative to the major axis, so they need no adjustment). Tessellated only
      // when drawn — keeps curvy imports compact.
      const c = apply(t, e.center.x, e.center.y);
      const m = apply(t, e.center.x + e.majorAxisEndPoint.x, e.center.y + e.majorAxisEndPoint.y);
      out.push({ kind: 'ellipse', center: c, major: { x: round2(m.x - c.x), y: round2(m.y - c.y) }, ratio, start, end: start + span, closed: full, color });
      break;
    }
    case 'SPLINE': {
      const e = ent as unknown as { controlPoints?: Vec2[]; fitPoints?: Vec2[]; knotValues?: number[]; degreeOfSplineCurve?: number; closed?: boolean };
      // B-splines transform affinely through their control points, so store them
      // parametrically (control points + knots) and tessellate only when drawn.
      if (e.controlPoints && e.controlPoints.length >= 2) {
        out.push({
          kind: 'spline',
          control: e.controlPoints.map((p) => apply(t, p.x, p.y)),
          degree: e.degreeOfSplineCurve && e.degreeOfSplineCurve > 0 ? e.degreeOfSplineCurve : 3,
          knots: e.knotValues,
          closed: !!e.closed,
          color,
        });
      } else if (e.fitPoints && e.fitPoints.length >= 2) {
        // No control points — approximate by a polyline through the fit points.
        out.push({ kind: 'polyline', points: e.fitPoints.map((p) => apply(t, p.x, p.y)), closed: !!e.closed, color });
      }
      break;
    }
    case 'TEXT': {
      const e = ent as ITextEntity;
      // A justified TEXT (72 centre / right / middle, 73 middle / top) hangs from
      // its second point; aligned and fit text (72 3 / 5) keep their first.
      const h = e.halign ?? 0, v = e.valign ?? 0;
      const align = h === 1 || h === 4 ? 'center' : h === 2 ? 'right' : undefined;
      const baseline = h === 4 || v === 2 ? 'middle' : v === 3 ? 'top' : undefined;
      const at = (align || baseline) && e.endPoint ? e.endPoint : e.startPoint;
      if (at) {
        out.push({
          kind: 'text',
          pos: apply(t, at.x, at.y),
          height: (e.textHeight || 100) * Math.abs(t.sy),
          value: e.text || '',
          ...(align && e.endPoint ? { align } : {}),
          ...(baseline && e.endPoint ? { baseline } : {}),
          color,
        });
      }
      break;
    }
    case 'MTEXT': {
      const e = ent as IMtextEntity;
      if (e.position) {
        out.push({
          kind: 'text',
          pos: apply(t, e.position.x, e.position.y),
          height: (e.height || 100) * Math.abs(t.sy),
          value: (e.text || '').replace(/\\[A-Za-z][^;]*;/g, '').replace(/[{}]/g, ''),
          color,
        });
      }
      break;
    }
    case 'INSERT': {
      if (depth > 4) break; // guard against pathological recursion
      const e = ent as IInsertEntity;
      const block = dxf.blocks?.[e.name];
      if (!block || !block.entities) break;
      const local = apply(t, e.position?.x ?? 0, e.position?.y ?? 0);
      const child: Transform = {
        tx: local.x,
        ty: local.y,
        sx: t.sx * (e.xScale || 1),
        sy: t.sy * (e.yScale || 1),
        rot: t.rot + (e.rotation || 0),
      };
      // Block geometry is defined relative to the block base point. BYBLOCK
      // children inherit the INSERT's colour.
      const base = block.position || { x: 0, y: 0, z: 0 };
      for (const be of block.entities) {
        convertEntity(shiftBase(be, base.x, base.y), dxf, child, out, depth + 1, color);
      }
      break;
    }
    default:
      break;
  }
}

/** Block entities are relative to the block base point; subtract it. */
function shiftBase(ent: IEntity, bx: number, by: number): IEntity {
  if (bx === 0 && by === 0) return ent;
  const clone: any = { ...ent };
  const shiftPt = (p: any) => (p ? { ...p, x: p.x - bx, y: p.y - by } : p);
  if (clone.vertices) clone.vertices = clone.vertices.map((v: any) => ({ ...v, x: v.x - bx, y: v.y - by }));
  if (clone.center) clone.center = shiftPt(clone.center);
  if (clone.position) clone.position = shiftPt(clone.position);
  if (clone.startPoint) clone.startPoint = shiftPt(clone.startPoint);
  return clone as IEntity;
}

/** A closed outline found in a DXF (a bench profile to import): its vertices with
 *  their bulges, as drawn, and the text standing nearest its middle (its label,
 *  e.g. "A"), if any. */
export interface DxfOutline { points: Array<{ x: number; y: number; bulge?: number }>; label?: string }

/** Every closed polyline of a DXF's model space, largest first; a label is a
 *  text within four times the outline's size of its middle. */
export function dxfClosedOutlines(dxfText: string): DxfOutline[] {
  const dxf = new DxfParser().parseSync(dxfText);
  if (!dxf) throw new Error('Could not parse DXF data.');
  const ents = (dxf.entities || []) as unknown as Array<Record<string, unknown>>;
  const texts = ents
    .filter((e) => (e.type === 'TEXT' || e.type === 'MTEXT') && typeof e.text === 'string')
    .map((e) => ({ text: String(e.text).replace(/\\[A-Za-z][^;]*;|[{}]/g, '').trim(), at: (e.startPoint ?? e.position) as Vec2 | undefined }))
    .filter((t) => t.text && t.at);
  const out: Array<DxfOutline & { area: number }> = [];
  for (const e of ents) {
    if (e.type !== 'LWPOLYLINE' && e.type !== 'POLYLINE') continue;
    const vs = (e.vertices as Array<{ x: number; y: number; bulge?: number }> | undefined) ?? [];
    const pts = vs.map((v) => ({ x: v.x, y: v.y, ...(v.bulge ? { bulge: v.bulge } : {}) }));
    const first = pts[0], last = pts[pts.length - 1];
    const closedByPoint = pts.length > 3 && first && last && Math.hypot(first.x - last.x, first.y - last.y) < 1e-6;
    if (!(e.shape || closedByPoint)) continue;
    if (closedByPoint) pts.pop();
    if (pts.length < 3) continue;
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const c = { x: (Math.min(...xs) + Math.max(...xs)) / 2, y: (Math.min(...ys) + Math.max(...ys)) / 2 };
    const near = [...texts].sort((a, b) => Math.hypot(a.at!.x - c.x, a.at!.y - c.y) - Math.hypot(b.at!.x - c.x, b.at!.y - c.y))[0];
    const reach = 4 * Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    const label = near && Math.hypot(near.at!.x - c.x, near.at!.y - c.y) <= reach ? near.text : undefined;
    out.push({ points: pts, label, area: profileArea(pts) });
  }
  return out.sort((a, b) => b.area - a.area).map(({ points, label }) => ({ points, ...(label ? { label } : {}) }));
}
